import { emailAccountProviderShortLabel } from "@v1/backend/logic/email/provider_page_sizes";
import { AtSign } from "lucide-react";
import type { ReactElement } from "react";

/** Official-style Gmail mark (envelope + multicolor M). */
export function GmailBrandIcon({
  className,
}: {
  readonly className?: string;
}): ReactElement {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      aria-hidden
      focusable="false"
    >
      <path
        fill="#4CAF50"
        d="M45 16.2 40 18.95 35 23.7V40h7c1.657 0 3-1.343 3-3V16.2z"
      />
      <path
        fill="#1E88E5"
        d="M3 16.2 6.614 17.91 13 23.7V40H6c-1.657 0-3-1.343-3-3V16.2z"
      />
      <path
        fill="#E53935"
        d="M35 11.2 24 19.45 13 11.2 12 17l1 6.7 11 8.25 11-8.25 1-6.7z"
      />
      <path
        fill="#C62828"
        d="M3 12.298V16.2l10 7.5V11.2L9.876 8.859A4.294 4.294 0 0 0 7.298 8h0C4.924 8 3 9.924 3 12.298z"
      />
      <path
        fill="#FBC02D"
        d="M45 12.298V16.2l-10 7.5V11.2l3.124-2.341A4.294 4.294 0 0 1 40.702 8h0C43.076 8 45 9.924 45 12.298z"
      />
    </svg>
  );
}

/** Official Microsoft 4-square logo. */
export function MicrosoftBrandIcon({
  className,
}: {
  readonly className?: string;
}): ReactElement {
  return (
    <svg
      viewBox="0 0 21 21"
      className={className}
      aria-hidden
      focusable="false"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/** Official WhatsApp mark (green silhouette). */
export function WhatsAppBrandIcon({
  className,
}: {
  readonly className?: string;
}): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden
      focusable="false"
    >
      <path
        fill="#25D366"
        d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.15h-.01c-1.53 0-3.03-.41-4.34-1.19l-.31-.18-3.23.85.86-3.15-.2-.32a8.21 8.21 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.48-1.38-1.73-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.84-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.41 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.29z"
      />
    </svg>
  );
}

/** List-row mark: Gmail / Microsoft / IMAP (@). */
export function EmailAccountProviderMark({
  provider,
}: {
  readonly provider: string | undefined;
}): ReactElement {
  const label: "Gmail" | "M365" | "IMAP" =
    emailAccountProviderShortLabel(provider);
  const title: string =
    label === "Gmail"
      ? "Google / Gmail"
      : label === "M365"
        ? "Microsoft 365"
        : "IMAP";

  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-action-500/[0.05]"
      title={title}
      aria-label={title}
    >
      {label === "Gmail" ? (
        <GmailBrandIcon className="h-4 w-4" />
      ) : label === "M365" ? (
        <MicrosoftBrandIcon className="h-4 w-4" />
      ) : (
        <AtSign className="h-4 w-4 text-primary/45" aria-hidden />
      )}
    </div>
  );
}
